import type { PolicyTask, TaskActionVisibility, TaskMode, TaskViewer } from './model';

const OWNER_WORK_STATUSES = ['claimed', 'in_progress', 'changes_requested'] as const;

export interface ReviewModeInput {
  uid: string | null;
  task: PolicyTask | null;
  canReview: boolean;
}

export interface ReviewModeResult {
  mode: TaskMode;
  canClaimReview: boolean;
}

/** Pure review/author mode calculation used by task detail and shell views. */
export function deriveTaskMode({ uid, task, canReview }: ReviewModeInput): ReviewModeResult {
  if (!uid || !task) return { mode: 'preview', canClaimReview: false };
  if (task.ownerId === uid && OWNER_WORK_STATUSES.includes(task.status as typeof OWNER_WORK_STATUSES[number])) {
    return { mode: 'work', canClaimReview: false };
  }
  if (task.status === 'in_review') {
    if (task.reviewerId === uid) return { mode: 'review', canClaimReview: false };
    if (canReview && task.reviewerId === null) return { mode: 'review', canClaimReview: true };
  }
  return { mode: 'preview', canClaimReview: false };
}

export function deriveTaskActionVisibility(
  task: PolicyTask,
  viewer: TaskViewer,
): TaskActionVisibility {
  const { mode, canClaimReview } = deriveTaskMode({
    uid: viewer.uid,
    task,
    canReview: viewer.canReview,
  });
  const isOwner = viewer.uid !== null && task.ownerId === viewer.uid;
  return {
    mode,
    canClaimReview,
    canEditResult: mode === 'work' || (viewer.isAdmin === true && task.status === 'in_review'),
    canRename: isOwner && OWNER_WORK_STATUSES.includes(task.status as typeof OWNER_WORK_STATUSES[number]),
    canRework: isOwner && task.status === 'approved',
    canReturnToQueue: isOwner && task.status === 'in_review' && task.reviewerId === null,
    canReleaseReview: viewer.uid !== null && task.status === 'in_review' && task.reviewerId === viewer.uid,
  };
}

export function canReviewTask(task: PolicyTask, viewer: TaskViewer): boolean {
  return viewer.uid !== null && viewer.canReview && task.status === 'in_review' && task.reviewerId === null;
}
